import axios from 'axios'
import { secureFetch } from './payloadTransport'
import { debounce } from '@/util/debounce'
import qs from 'qs'
import CryptoJS from 'crypto-js';
import JSEncrypt from 'jsencrypt';
import Vue from 'vue';
import { Message } from 'element-ui';
import store from '@/store';
import router from '@/router'
import { i18n } from '@/util/i18n'
import { cookieAuthEnabled, getMemberCsrf, hasCookieSessionMarker } from './authMode'
import { apiError, unwrapApiResponse, removeLegacyCredentials } from './apiResponse'

const publicKey = `-----BEGIN PUBLIC KEY-----
MIGfMA0GCSqGSIb3DQEBAQUAA4GNADCBiQKBgQC1jRXekh2TNhlNvme6bHdgJZBn
a7+ymEg6aGWHCUhc5a9YDyDRm//tEALA7W3u2Tpba75hOfKS2ifmPfOrqcGNLEEt
n3tizlKoXnsnU1cXxspnrMSxoF/c5aCvgEnWyu8iPUAKh1PWbrjKBTBirF9aUmfH
HEv0faNpgFWYkb4l0wIDAQAB
-----END PUBLIC KEY-----`;

const encryption = !cookieAuthEnabled && process.env.NODE_ENV !== 'development';

const encryptSessionKey = (publicKey, sessionKey) => {
  const encrypt = new JSEncrypt();
  encrypt.setPublicKey(publicKey);
  return encrypt.encrypt(sessionKey);
};

const toArrayBuffer = (encrypted) => {
  const uint8Array = new Uint8Array(encrypted.sigBytes);
  for (let i = 0; i < encrypted.sigBytes; i++) {
    uint8Array[i] = (encrypted.words[Math.floor(i / 4)] >> (24 - (i % 4) * 8)) & 0xff;  
  }
  return uint8Array.buffer;
}

// 使用会话密钥加密数据
const encryptData = (data, key) => {
  const iv = CryptoJS.lib.WordArray.random(16); // 生成随机 IV
  const encrypted = CryptoJS.AES.encrypt(JSON.stringify(data), CryptoJS.enc.Utf8.parse(key), {
    iv: iv,
    mode: CryptoJS.mode.CBC,
    padding: CryptoJS.pad.Pkcs7
  });

  // 将 IV 和密文拼接成二进制数据
  return iv.concat(encrypted.ciphertext);
};

// 使用会话密钥解密数据
const decryptData = (encryptedData, key, isJson = false) => {
  const combined = CryptoJS.lib.WordArray.create(encryptedData)
  const iv = CryptoJS.lib.WordArray.create(combined.words.slice(0, 4)); // 提取前 16 字节作为 IV
  const cipherText = CryptoJS.lib.WordArray.create(combined.words.slice(4)); // 剩余的作为密文

  const decrypted = CryptoJS.AES.decrypt({ciphertext: cipherText}, CryptoJS.enc.Utf8.parse(key), {
    iv: iv,
    mode: CryptoJS.mode.CBC,
    padding: CryptoJS.pad.Pkcs7
  });

  if (isJson) {
    try {
      const decryptedString = decrypted.toString(CryptoJS.enc.Utf8);
      return JSON.parse(decryptedString);
    } catch (error) {
      console.error('Failed to parse JSON:', error);
      throw error;
    }
  } else {
    return toArrayBuffer(decrypted);
  }
};

const getSessionKey = () => {
  let sessionKey = store.state.sessionKey;
  if (!sessionKey) {
    store.dispatch('generateSessionKey');
    sessionKey = store.state.sessionKey;
  }
  return sessionKey;
}

const clearLoginInfo = () => {
  store.commit('setSessionKey', null);
  store.commit('vuexSignOut');
}

const request = axios.create({
  baseURL: process.env.VUE_APP_API_BASE_URL,
  timeout: 1000 * 1800,
  withCredentials: true
})

/**
 * 请求拦截
 */
request.interceptors.request.use(config => {
  if (cookieAuthEnabled) {
    // New API: session is an HttpOnly cookie; legacy bearer/envelope headers are forbidden.
    removeLegacyCredentials(config.headers)
    if (['post', 'put', 'patch', 'delete'].includes(String(config.method).toLowerCase())
        && config.url !== '/api/member/login') {
      const proof = getMemberCsrf()
      if (hasCookieSessionMarker() && !proof) {
        return Promise.reject(new Error('Member CSRF proof unavailable'))
      }
      if (proof) config.headers['X-CSRF-Token'] = proof
    }
    config.headers['Accept-Language'] = i18n.locale
    config.paramsSerializer = params => qs.stringify(params)
    config.adapter = async requestConfig => {
      const path = new URL(requestConfig.url, window.location.origin)
      const controller = new AbortController()
      const abort = () => controller.abort()
      if (requestConfig.signal?.aborted) abort()
      requestConfig.signal?.addEventListener('abort', abort, { once: true })
      const timer = requestConfig.timeout > 0 ? setTimeout(abort, requestConfig.timeout) : null
      try {
        const response = await secureFetch(path.href, {
          method: requestConfig.method,
          headers: requestConfig.headers.toJSON ? requestConfig.headers.toJSON() : requestConfig.headers,
          body: requestConfig.data,
          signal: controller.signal
        }, { query: requestConfig.params || {}, required: process.env.VUE_APP_PAYLOAD_MODE === 'required' })
        const data = requestConfig.responseType === 'blob' ? await response.blob()
          : requestConfig.responseType === 'arraybuffer' ? await response.arrayBuffer() : await response.json()
        const result = { data, status: response.status, statusText: response.statusText,
          headers: Object.fromEntries(response.headers.entries()), config: requestConfig }
        if (!response.ok) throw Object.assign(new Error(data.message || '请求失败'), { response: result, config: requestConfig })
        return result
      } finally {
        if (timer) clearTimeout(timer)
        requestConfig.signal?.removeEventListener('abort', abort)
      }
    }
    return config
  }
  config.headers['token'] =  localStorage.getItem('token') || ''

  let sessionKey = getSessionKey()
  config.headers['X-Session-Key'] = encryptSessionKey(publicKey, sessionKey);

  const language = i18n.locale;
  config.headers['Accept-Language'] = language;

  if (encryption) {
    config.responseType = 'arraybuffer';

    // 加密请求数据
    if (config.method === 'post') {
      config.headers['Content-Type'] = 'application/octet-stream';

      let data = config.data ?? ''
      let encrypted = encryptData(data, sessionKey);
      config.data = toArrayBuffer(encrypted);
    } else if (config.method === 'get') {
      let data = config.params ?? ''
      const encryptedParams = encryptData(data, sessionKey);
      config.params = { '_e': CryptoJS.enc.Base64.stringify(encryptedParams) };
    }
  } else {
    // get序列化
    config.paramsSerializer = function (params) {
      return qs.stringify(params)
    }
  }

  return config
}, error => {
  return Promise.reject(error)
})

/**
 * 响应拦截
 */
request.interceptors.response.use(response => {
  if (cookieAuthEnabled) {
    // Binary endpoints have their own download handling; ordinary API responses
    // must carry the new success envelope, never silently accept code 0/401.
    if (['blob', 'arraybuffer'].includes(response.config && response.config.responseType)) return response.data
    try {
      return unwrapApiResponse(response)
    } catch (error) {
      if (error.code === 401) clearLoginInfo()
      return Promise.reject(error)
    }
  }
  if (encryption) {
    const sessionKey = store.state.sessionKey;
    const isJSON = (response.headers['content-type'] && response.headers['content-type'].includes('application/octet-stream'));
    var decryptedData = decryptData(response.data, sessionKey, isJSON);
    response.data = decryptedData;
  }
  
  if(response.data.code === 0){
    let msg = response.data.msg ?? "request failed"
    Message.error(msg)
    return Promise.reject(response)
  }

  return response.data
}, error => {
  if (error.response && error.response.status === 401) {
    clearLoginInfo()

    Vue.nextTick(() => {
      const navigateToLogin = debounce(() => {
        if (router.currentRoute.name !== 'login') {
          router.replace({ name: 'login' }).catch((e) => {
            console.log(e);
          });
        }
      }, 500); // Adjust the debounce delay as needed
      navigateToLogin();
    });
  } 
  return Promise.reject(cookieAuthEnabled && error.response ? apiError(error.response) : error)
})

export { request, getSessionKey };
